import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  TIMELINE_ENTITY_TYPES,
  TIMELINE_EVENT_TYPES,
  fetchTimelineStats,
  listEntityTimeline,
  listRecentTimeline,
  logTimelineEvent,
  timelineEntityMeta,
  timelineEventMeta,
  type CoreTimelineEvent,
} from '@/lib/coreTimeline';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function TimelineTab({ org }: Props) {
  const { user } = useAuth();
  const [items, setItems] = useState<CoreTimelineEvent[]>([]);
  const [stats, setStats] = useState<{ total: number; byEntityType: Record<string, number> }>({ total: 0, byEntityType: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [aiOnly, setAiOnly] = useState(false);
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterEntityId, setFilterEntityId] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entityType: 'customer', entityId: '', eventType: 'custom', title: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, st] = await Promise.all([listRecentTimeline(org.id, 120), fetchTimelineStats(org.id)]);
      setItems(list);
      setStats(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = items;
    if (aiOnly) {
      list = list.filter((e) => e.entity_type === 'ai_chat');
    } else if (filterEntityType && filterEntityId.trim()) {
      list = list.filter((e) => e.entity_type === filterEntityType && e.entity_id === filterEntityId.trim());
    } else if (entityType !== 'all') {
      list = list.filter((e) => e.entity_type === entityType);
    }
    return list;
  }, [items, entityType, filterEntityType, filterEntityId, aiOnly]);

  const submit = async () => {
    if (!form.title.trim() || !form.entityId.trim()) return;
    setSaving(true);
    try {
      await logTimelineEvent({
        tenantId: org.tenant_id,
        organizationId: org.id,
        entityType: form.entityType,
        entityId: form.entityId.trim(),
        eventType: form.eventType,
        title: form.title.trim(),
        description: form.description.trim() || null,
        actorUserId: user?.id ?? null,
        actorName: user?.name || 'Vous',
      });
      setOpen(false);
      setForm({ entityType: form.entityType, entityId: '', eventType: 'custom', title: '', description: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const search = async () => {
    if (!filterEntityType || !filterEntityId.trim()) { load(); return; }
    setLoading(true);
    setError('');
    try {
      setItems(await listEntityTimeline(org.id, filterEntityType, filterEntityId.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Timeline centrale : toutes les interactions d’une entité au même endroit (RDV, devis, facture, paiement, message…),
          y compris les <span className="font-semibold text-foreground-700">recherches de vos clients via l’assistant IA</span>.
          Réutilisable par <span className="font-semibold text-foreground-700">CRM, Finance, Support et AI</span>.
        </p>
        <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Enregistrer un événement
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {TIMELINE_ENTITY_TYPES.slice(0, 5).map((e) => (
          <div key={e.key} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
            <div className="flex items-center justify-between">
              <i className={`${e.icon} text-lg text-primary-600`}></i>
              <span className="text-sm font-bold text-foreground-900">{stats.byEntityType[e.key] || 0}</span>
            </div>
            <p className="text-xs font-medium text-foreground-700 mt-1.5">{e.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-3 flex flex-col sm:flex-row gap-2 sm:items-end">
        <label className="block flex-1">
          <span className="block text-xs font-medium text-foreground-600 mb-1">Filtrer par entité</span>
          <select className={INPUT} value={filterEntityType} onChange={(e) => setFilterEntityType(e.target.value)}>
            <option value="">— Toutes —</option>
            {TIMELINE_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
          </select>
        </label>
        <label className="block flex-1">
          <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant</span>
          <input className={INPUT} value={filterEntityId} onChange={(e) => setFilterEntityId(e.target.value)} placeholder="Ex : 100234" />
        </label>
        <button onClick={search} className="px-4 py-2 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer">Rechercher</button>
        <button onClick={() => { setFilterEntityType(''); setFilterEntityId(''); setEntityType('all'); setAiOnly(false); load(); }} className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">Réinitialiser</button>
      </div>

      {!filterEntityType && (
        <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          <button onClick={() => { setAiOnly(true); setEntityType('all'); setFilterEntityType(''); setFilterEntityId(''); }} className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${aiOnly ? 'bg-accent-500 text-background-50' : 'text-accent-700 hover:text-accent-800'}`}><i className="ri-robot-2-line"></i> Recherches IA</button>
          <button onClick={() => { setAiOnly(false); setEntityType('all'); }} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${!aiOnly && entityType === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Tous</button>
          {TIMELINE_ENTITY_TYPES.map((e) => (
            <button key={e.key} onClick={() => { setAiOnly(false); setEntityType(e.key); }} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${entityType === e.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{e.label}</button>
          ))}
        </div>
      )}

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-time-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun événement dans la timeline.</p>
        </div>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-background-200/80"></div>
          <div className="space-y-3">
            {filtered.map((ev) => {
              const meta = timelineEventMeta(ev.event_type);
              const ent = timelineEntityMeta(ev.entity_type);
              return (
                <div key={ev.id} className="relative">
                  <span className={`absolute -left-[18px] top-1.5 w-4 h-4 rounded-full flex items-center justify-center ${meta.className}`}><i className={`${meta.icon} text-[10px]`}></i></span>
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-900">{ev.title}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                      {ent && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap"><i className={ent.icon}></i>{ent.label} #{ev.entity_id}</span>}
                    </div>
                    {ev.description && <p className="text-xs text-foreground-600 mt-1">{ev.description}</p>}
                    <p className="text-[11px] text-foreground-400 mt-1">
                      {ev.actor_name ? `${ev.actor_name} · ` : ''}
                      {ev.occurred_at ? new Date(ev.occurred_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      {ev.amount != null ? ` · ${ev.amount} ${ev.currency || ''}` : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {open && (
        <Modal
          title="Enregistrer un événement"
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={submit} disabled={saving || !form.title.trim() || !form.entityId.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
                  {TIMELINE_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant</span>
                <input className={INPUT} value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} placeholder="Ex : 100234" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Type d’événement</span>
              <select className={INPUT} value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })}>
                {TIMELINE_EVENT_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre</span>
              <input className={INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Devis envoyé" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Description (optionnel)</span>
              <textarea rows={2} className={`${INPUT} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} placeholder="Détails…" />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}